// Shared building blocks for the reward-program settings cards (Loyalty +
// Referral). Extracted from LoyaltySettingsCard so the two configs stay one
// vocabulary: the same value+unit control, the same reward editor (free
// credits vs package discount) and the same dirty check — a reward is a
// reward, whether a milestone or a referral side pays it out. The row chrome
// (SettingRow) lives in settingsRows.tsx with the other settings pages'.

import { Form, InputNumber, Select } from "antd";
import { translate } from "shared/languages";
import { formatCurrency } from "shared/utils";
import {
  ExpiryUnit,
  LoyaltyClassScope,
  LoyaltyDiscountMode,
  LoyaltyDiscountScope,
  LoyaltyRewardType,
} from "shared/types/enum";
import { sectionLabelStyle } from "components/NavSectionLabel.component";

export type SelectOption = { value: string; label: string };
export type SelectOptionGroup = {
  label: string;
  title: string;
  options: SelectOption[];
};

// "Dirty" for the reward forms: compares every value in the form store
// (Form.useWatch with preserve), ignoring key order and undefined. Comparing
// only the mounted fields against the full hydrated object marked the form
// changed on load whenever a conditional field was hidden.
const canonical = (v: unknown): unknown => {
  if (Array.isArray(v)) return v.map(canonical);
  if (v && typeof v === "object") {
    const o = v as Record<string, unknown>;
    return Object.fromEntries(
      Object.keys(o)
        .filter((k) => o[k] !== undefined)
        .sort()
        .map((k) => [k, canonical(o[k])]),
    );
  }
  return v;
};
export const sameFormValues = (a: unknown, b: unknown) =>
  JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));

// The shared section-label style (same values this card always used).
export const eyebrowStyle = sectionLabelStyle;

// Built at render time, never module scope — translate() is bare i18next.t
// and resolves wrongly before i18n init (see the package-products gotcha).
export const buildUnitOptions = (): SelectOption[] => [
  { value: ExpiryUnit.Day, label: translate("expiry_unit.day") },
  { value: ExpiryUnit.Month, label: translate("expiry_unit.month") },
  { value: ExpiryUnit.Year, label: translate("expiry_unit.year") },
];

// Unit dropdown living inside the value InputNumber's addon, so the pair
// reads as one control.
export const unitAddon = (namePath: (string | number)[]) => (
  <Form.Item name={namePath} noStyle>
    <Select options={buildUnitOptions()} style={{ width: 92 }} />
  </Form.Item>
);

// The reward-type-specific field block: free credits (qty + class scope +
// selected classes) or package discount (mode + value + scope + selected
// products). `namePath` is the Form.Item-relative path prefix (inside a
// Form.List that's `[name]`; for a plain nested object it's the object key),
// `fullPath` is the absolute path getFieldValue needs — inside a Form.List
// they differ, elsewhere they're the same.
export const RewardTypeSpecificFields = ({
  namePath,
  fullPath,
  classOptions,
  productOptions,
}: {
  namePath: (string | number)[];
  fullPath: (string | number)[];
  classOptions: SelectOption[];
  productOptions: SelectOptionGroup[];
}) => (
  <Form.Item noStyle shouldUpdate>
    {({ getFieldValue }) => {
      const rewardType = getFieldValue([...fullPath, "reward_type"]);
      if (rewardType === LoyaltyRewardType.FreeCredit) {
        const scope = getFieldValue([...fullPath, "class_scope"]);
        return (
          <div style={{ display: "contents" }}>
            <Form.Item
              name={[...namePath, "credit_qty"]}
              label={translate("loyalty.milestones.credit_qty")}
              rules={[
                {
                  required: true,
                  type: "integer",
                  min: 1,
                  message: translate("form.error.required"),
                },
              ]}
            >
              <InputNumber min={1} precision={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name={[...namePath, "class_scope"]}
              label={translate("loyalty.milestones.class_scope")}
              rules={[
                {
                  required: true,
                  message: translate("form.error.required"),
                },
              ]}
            >
              <Select
                options={[
                  {
                    value: LoyaltyClassScope.All,
                    label: translate("loyalty.scope.all_classes"),
                  },
                  {
                    value: LoyaltyClassScope.AllOpen,
                    label: translate("loyalty.scope.all_open"),
                  },
                  {
                    value: LoyaltyClassScope.AllPrivate,
                    label: translate("loyalty.scope.all_private"),
                  },
                  {
                    value: LoyaltyClassScope.Selected,
                    label: translate("loyalty.scope.selected"),
                  },
                ]}
              />
            </Form.Item>
            {scope === LoyaltyClassScope.Selected && (
              <Form.Item
                name={[...namePath, "class_type_ids"]}
                label={translate("loyalty.milestones.classes")}
                style={{ gridColumn: "1 / -1" }}
                rules={[
                  {
                    required: true,
                    message: translate("loyalty.error.scope_classes"),
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  options={classOptions}
                  optionFilterProp="label"
                />
              </Form.Item>
            )}
          </div>
        );
      }
      if (rewardType === LoyaltyRewardType.Discount) {
        const mode = getFieldValue([...fullPath, "discount_mode"]);
        const scope = getFieldValue([...fullPath, "discount_scope"]);
        return (
          <div style={{ display: "contents" }}>
            <Form.Item
              name={[...namePath, "discount_mode"]}
              label={translate("loyalty.milestones.discount_mode")}
              rules={[
                {
                  required: true,
                  message: translate("form.error.required"),
                },
              ]}
            >
              <Select
                options={[
                  {
                    value: LoyaltyDiscountMode.Percent,
                    label: translate("loyalty.discount.percent"),
                  },
                  {
                    value: LoyaltyDiscountMode.Fixed,
                    label: translate("loyalty.discount.fixed"),
                  },
                ]}
              />
            </Form.Item>
            <Form.Item
              name={[...namePath, "discount_value"]}
              label={translate("loyalty.milestones.discount_value")}
              rules={[
                {
                  required: true,
                  type: "integer",
                  min: 1,
                  ...(mode === LoyaltyDiscountMode.Percent ? { max: 100 } : {}),
                  message: translate("form.error.required"),
                },
              ]}
            >
              {mode === LoyaltyDiscountMode.Percent ? (
                <InputNumber
                  min={1}
                  max={100}
                  precision={0}
                  style={{ width: "100%" }}
                  addonAfter="%"
                />
              ) : (
                // Rupiah amounts format with thousand separators inside the
                // input (the package price field's treatment).
                <InputNumber
                  min={1}
                  style={{ width: "100%" }}
                  formatter={(value?: number | string) =>
                    value ? formatCurrency(Number(value)) : ""
                  }
                  parser={(value?: string) =>
                    value
                      ? parseInt(value.replace(/[Rp,.\s]/g, ""), 10) || 0
                      : 0
                  }
                />
              )}
            </Form.Item>
            <Form.Item
              name={[...namePath, "discount_scope"]}
              label={translate("loyalty.milestones.discount_scope")}
              rules={[
                {
                  required: true,
                  message: translate("form.error.required"),
                },
              ]}
            >
              <Select
                options={[
                  {
                    value: LoyaltyDiscountScope.All,
                    label: translate("loyalty.scope.all"),
                  },
                  {
                    value: LoyaltyDiscountScope.Packages,
                    label: translate("loyalty.scope.all_packages"),
                  },
                  {
                    value: LoyaltyDiscountScope.Memberships,
                    label: translate("loyalty.scope.all_memberships"),
                  },
                  {
                    value: LoyaltyDiscountScope.Products,
                    label: translate("loyalty.scope.products"),
                  },
                ]}
              />
            </Form.Item>
            {scope === LoyaltyDiscountScope.Products && (
              <Form.Item
                name={[...namePath, "product_ids"]}
                label={translate("loyalty.milestones.products")}
                style={{ gridColumn: "1 / -1" }}
                rules={[
                  {
                    required: true,
                    message: translate("loyalty.error.scope_products"),
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  options={productOptions}
                  optionFilterProp="label"
                />
              </Form.Item>
            )}
          </div>
        );
      }
      return null;
    }}
  </Form.Item>
);
